/**
 * Force hero video autoplay on mobile devices
 * This script ensures videos with the hero__video class autoplay on all devices
 * Uses multiple strategies to bypass browser autoplay restrictions
 * Includes HLS.js support for desktop browsers
 */

// Load HLS.js library if needed
let hlsLibraryLoaded = false;

function loadHlsLibrary() {
  return new Promise((resolve, reject) => {
    if (window.Hls) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
    script.onload = () => {
      hlsLibraryLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

class HeroVideoAutoplay extends HTMLElement {
  constructor() {
    super();
    this.attemptCount = 0;
    this.maxAttempts = 10;
    this.playAttempted = false;
  }

  async connectedCallback() {
    // Load HLS.js first if needed
    await this.setupHlsSupport();

    // Try immediately
    this.attemptAutoplay();

    // Retry when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.attemptAutoplay();
      }
    });

    // Retry on any user interaction
    const interactions = ['touchstart', 'touchend', 'click', 'scroll', 'mousemove'];
    interactions.forEach(event => {
      document.addEventListener(event, () => this.attemptAutoplay(), {
        once: true,
        passive: true
      });
    });

    // Retry when video metadata is loaded
    const videos = this.querySelectorAll('video.hero__video');
    videos.forEach(video => {
      video.addEventListener('loadedmetadata', () => this.attemptAutoplay());
      video.addEventListener('canplay', () => this.attemptAutoplay());
      video.addEventListener('canplaythrough', () => this.attemptAutoplay());
    });

    // Aggressive retry with exponential backoff
    this.startRetryLoop();
  }

  async setupHlsSupport() {
    const videos = this.querySelectorAll('video.hero__video');

    for (const video of videos) {
      const source = video.querySelector('source');
      if (!source) continue;

      const videoSrc = source.src;
      const isHls = videoSrc.includes('.m3u8');

      if (isHls) {
        // Check if browser supports HLS natively (Safari, iOS)
        if (video.canPlayType('application/x-mpegURL') || video.canPlayType('application/vnd.apple.mpegurl')) {
          console.log('✅ Native HLS support detected');
          continue;
        }

        // For other browsers, use HLS.js
        try {
          await loadHlsLibrary();

          if (window.Hls && window.Hls.isSupported()) {
            console.log('✅ Loading video with HLS.js');
            const hls = new window.Hls({
              enableWorker: true,
              lowLatencyMode: false,
              backBufferLength: 90
            });

            hls.loadSource(videoSrc);
            hls.attachMedia(video);

            hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
              console.log('✅ HLS manifest loaded');
              video.dispatchEvent(new Event('canplay'));
            });

            hls.on(window.Hls.Events.ERROR, (event, data) => {
              if (data.fatal) {
                console.error('❌ Fatal HLS error:', data);
              }
            });

            // Store HLS instance for cleanup
            video.hlsInstance = hls;
          }
        } catch (error) {
          console.error('❌ Failed to load HLS.js:', error);
        }
      }
    }
  }

  attemptAutoplay() {
    if (this.attemptCount >= this.maxAttempts) return;
    this.attemptCount++;

    const videos = this.querySelectorAll('video.hero__video');

    videos.forEach(video => {
      // Force all attributes - both via setAttribute and property
      video.setAttribute('autoplay', '');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('preload', 'auto');
      video.removeAttribute('controls');

      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.loop = true;
      video.controls = false;
      video.defaultMuted = true;

      // Set volume to 0 as extra precaution
      video.volume = 0;

      // Skip if already playing
      if (!video.paused) {
        console.log('✅ Video already playing');
        return;
      }

      // Load the video if not loaded
      if (video.readyState < 2) {
        video.load();
      }

      // Attempt to play
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('✅ Hero video autoplay successful on attempt', this.attemptCount);
            this.playAttempted = true;
            this.attemptCount = this.maxAttempts; // Stop retrying

            // Hide poster image when video plays
            const wrapper = video.closest('.hero__video-wrapper');
            if (wrapper) {
              wrapper.classList.add('video-playing');
              const poster = wrapper.querySelector('.hero__video-poster');
              if (poster) {
                poster.style.opacity = '0';
              }
            }
          })
          .catch(error => {
            console.warn(`⚠️ Autoplay attempt ${this.attemptCount} blocked:`, error.message);

            // If it's a NotAllowedError, we need user interaction
            if (error.name === 'NotAllowedError') {
              console.log('🔄 Waiting for user interaction...');
            }
          });
      }
    });
  }

  startRetryLoop() {
    // Try every 500ms for the first 5 seconds
    const retryIntervals = [100, 200, 300, 500, 1000, 1000, 1000, 2000, 3000];

    retryIntervals.forEach((delay, index) => {
      setTimeout(() => {
        if (this.attemptCount < this.maxAttempts && !this.playAttempted) {
          this.attemptAutoplay();
        }
      }, retryIntervals.slice(0, index).reduce((a, b) => a + b, 0));
    });
  }
}

if (!customElements.get('hero-video-autoplay')) {
  customElements.define('hero-video-autoplay', HeroVideoAutoplay);
}

// Also try a global approach for maximum compatibility
document.addEventListener('DOMContentLoaded', () => {
  const heroVideos = document.querySelectorAll('video.hero__video');

  heroVideos.forEach(video => {
    // Ensure muted
    video.muted = true;
    video.volume = 0;
    video.defaultMuted = true;

    // Helper function to play video and hide poster
    const playAndHidePoster = () => {
      return video.play().then(() => {
        const wrapper = video.closest('.hero__video-wrapper');
        if (wrapper) {
          wrapper.classList.add('video-playing');
          const poster = wrapper.querySelector('.hero__video-poster');
          if (poster) poster.style.opacity = '0';
        }
      }).catch(err => console.warn('Play failed:', err));
    };

    // Try to play immediately
    playAndHidePoster().catch(() => {
      console.log('Initial autoplay blocked, setting up click handlers');
    });

    // ALWAYS add click handlers to video and wrapper (for Low Power Mode)
    const wrapper = video.closest('.hero__video-wrapper');

    const handleInteraction = (e) => {
      if (video.paused) {
        e.preventDefault();
        e.stopPropagation();
        playAndHidePoster();
      }
    };

    // Add listeners to both video and wrapper
    video.addEventListener('click', handleInteraction);
    video.addEventListener('touchend', handleInteraction);

    if (wrapper) {
      wrapper.addEventListener('click', handleInteraction);
      wrapper.addEventListener('touchend', handleInteraction);

      // Also add to poster if it exists
      const poster = wrapper.querySelector('.hero__video-poster');
      if (poster) {
        poster.style.pointerEvents = 'auto';
        poster.style.cursor = 'pointer';
        poster.addEventListener('click', handleInteraction);
        poster.addEventListener('touchend', handleInteraction);
      }
    }
  });
});
